import { IApiErrorResponse, IApiStandardResponse, IRequestPaginationData } from '@/core/models/api/api.models';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { I_API_GET_registrazioneConvenzionatoDaCompletare, I_API_GET_registrazionePromotoreDaCompletare } from '@models/apiresponses.models';
import { OtpChannel } from '@models/custom-types.model';
import {
    Comune,
    I_API_GET_IndirizzoDomicilio, I_API_GET_IndirizzoResidenza,
    I_API_PUT_IndirizzoDomicilio,
    I_API_PUT_IndirizzoResidenza,
    IApiGetIndirizzoSpedizione,
    IApiPutIndirizzoSpedizione,
    IndirizzoDomicilio,
    IndirizzoResidenza,
    IndirizzoSpedizione
} from '@models/indirizzo.model';
import { IConsensi } from '@models/privacy.model';
import { EMPTY_USER, IApiConvenzionatoPratiche, IApiElencoPratiche, IApiProfilo, IApiPromotorePratiche, IPraticaConvenzionato, IProfilo, ISegnalatorePromotore, IUser, IUserApiResponse, Profilo, ProfiloRuolo, RuoloAttivo, User } from '@models/user.model';
import { Console } from '@shared/providers/custom-logger';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { ApiService } from './api/api.service';
import { AuthService } from './auth.service';
import { MOCK_PRATICHE_CONVENZIONATO, MOCK_SEGNALATORI_PROMOTORE } from './mock-pratiche.data';
import { AppStore } from './store/app.store';
import { ToastService } from './toast.service';
import { UserRepository } from './user.repository.service';

/**
 * Servizio per la gestione delle operazioni relative all'utente, inclusi profili, indirizzi, consensi privacy e registrazioni.
 *
 * Responsabilità principali:
 * - Recupero e aggiornamento dati utente tramite API
 * - Gestione dei profili utente (promotore, convenzionato, manifestazione)
 * - Gestione degli indirizzi (residenza, domicilio, spedizione)
 * - Gestione dei consensi privacy
 * - Verifica e completamento registrazioni
 * - Gestione dei contatti utente (email, cellulare)
 *
 * Dipendenze:
 * - ApiService: per le chiamate HTTP alle API
 * - ToastService: per le notifiche utente
 * - UserRepository: per la persistenza locale dei dati utente
 * - Router: per la navigazione nell'applicazione
 * - AppStore: per la gestione dello stato globale
 *
 * @class UserService
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  /** Etichetta per i log del servizio */
  private log_label = 'UserService';

  /** Flag per utilizzare dati mock invece delle API (per test grafici) */
  private USE_MOCK_DATA = false;

  /** Subject per l'utente corrente */
  private currentUser = new BehaviorSubject<User>(new User(EMPTY_USER));
  
  /** Subject per il profilo corrente */
  private currentProfilo = new BehaviorSubject<IProfilo | null>(null);

  // Servizi iniettati
  private router = inject(Router);
  public toastService: ToastService = inject(ToastService);
  private apiService = inject(ApiService);
  private userRepository = inject(UserRepository);
  private appStore = inject(AppStore);
  private authService = inject(AuthService);

  constructor() {
    // Inizializza i BehaviorSubject con i dati dallo store applicativo
    this.initializeFromStore();
  }

  /**
   * Inizializza i BehaviorSubject con i dati dallo store applicativo.
   */
  private initializeFromStore(): void {
    const user = this.userRepository.getUser();
    if (user && user.id > 0) {
      this.currentUser.next(user);
    }
    
    const activeRole = this.userRepository.getActiveRole();
    if (activeRole && activeRole !== '' as RuoloAttivo) {
      const profilo = this.userRepository.getProfilo(activeRole as ProfiloRuolo);
      if (profilo) {
        this.currentProfilo.next(profilo);
      }
    }
  }

  // ==================== GETTER PER OBSERVABLE ====================
  
  /**
   * Restituisce un Observable per l'utente corrente.
   * @returns Observable<User> - Observable dell'utente corrente
   */
  get user$(): Observable<User> {
    return this.currentUser.asObservable();
  }

  /**
   * Observable per il profilo corrente
   * @returns Observable<IProfilo | null> - Observable del profilo corrente
   */
  get profilo$(): Observable<IProfilo | null> {
    return this.currentProfilo.asObservable();
  }

  /**
   * Observable per tutti i profili dell'utente
   * @returns Observable<IProfilo[]> - Observable di tutti i profili
   */
  get profili$(): Observable<IProfilo[]> {
    return this.currentUser.pipe(
      map(user => user.profili || [])
    );
  }

  /**
   * Observable per il ruolo attivo
   * @returns Observable<RuoloAttivo | null> - Observable del ruolo attivo
   */
  get activeRole$(): Observable<RuoloAttivo | null> {
    return this.currentUser.pipe(
      map(user => {
        const activeRole = this.userRepository.getActiveRole();
        return activeRole;
      })
    );
  }

  // ==================== METODI DI SINCRONIZZAZIONE ====================
  
  /**
   * Aggiorna l'utente e notifica tutti i sottoscrittori
   * @param user - L'utente da aggiornare
   */
  private updateUserAndNotify(user: User): void {
    Console.log(this.log_label + '.updateUserAndNotify', 'aggiornamento utente e notifica', 'std');
    this.currentUser.next(user);
    this.userRepository.saveUser(user);
  }

  /**
   * Aggiorna il profilo e notifica tutti i sottoscrittori
   * @param profilo - Il profilo da aggiornare
   */
  private updateProfiloAndNotify(profilo: IProfilo | null): void {
    Console.log(this.log_label + '.updateProfiloAndNotify', 'aggiornamento profilo e notifica', 'std');
    this.currentProfilo.next(profilo);
    if (profilo) {
      this.userRepository.saveProfilo(profilo);
    }
  }

  /**
   * Aggiorna i profili e notifica tutti i sottoscrittori
   * @param profili - I profili da aggiornare
   */
  private updateProfiliAndNotify(profili: IProfilo[]): void {
    Console.log(this.log_label + '.updateProfiliAndNotify', 'aggiornamento profili e notifica', 'std');
    const currentUser = this.currentUser.getValue();
    if (currentUser) {
      currentUser.profili = profili;
      this.updateUserAndNotify(currentUser);
    }
    this.userRepository.saveProfili(profili);
  }

  // ==================== METODI PUBBLICI PER AGGIORNAMENTI ====================
  
  /**
   * Metodo pubblico per aggiornare l'utente
   * @param user - L'utente da aggiornare
   */
  updateUser(user: User): void {
    this.updateUserAndNotify(user);
  }

  /**
   * Metodo pubblico per aggiornare il profilo corrente
   * @param profilo - Il profilo da aggiornare
   */
  updateProfilo(profilo: IProfilo): void {
    this.updateProfiloAndNotify(profilo);
  }

  /**
   * Metodo pubblico per aggiornare tutti i profili
   * @param profili - I profili da aggiornare
   */
  updateProfili(profili: IProfilo[]): void {
    this.updateProfiliAndNotify(profili);
  }

  /**
   * Metodo pubblico per aggiornare il ruolo attivo
   * @param ruolo - Il ruolo da impostare come attivo
   */
  updateActiveRole(ruolo: RuoloAttivo): void {
    Console.log(this.log_label + '.updateActiveRole', 'aggiornamento ruolo attivo: ' + ruolo, 'std');
    this.userRepository.setActiveRole(ruolo);
    
    // Aggiorna anche il profilo corrente se disponibile
    const profilo = this.userRepository.getProfilo(ruolo);
    if (profilo) {
      this.updateProfiloAndNotify(profilo);
    }
  }

  // ==================== METODI DI UTILITÀ ====================
  
  /**
   * Recupera l'utente corrente (metodo sincrono)
   * @returns User - L'utente corrente
   */
  getCurrentUser(): User {
    return this.currentUser.getValue();
  }

  /**
   * Recupera il profilo corrente (metodo sincrono)
   * @returns IProfilo | null - Il profilo corrente
   */
  getCurrentProfilo(): IProfilo | null {
    return this.currentProfilo.getValue();
  }

  /**
   * Recupera tutti i profili (metodo sincrono)
   * @returns IProfilo[] - Tutti i profili dell'utente
   */
  getCurrentProfili(): IProfilo[] {
    const user = this.currentUser.getValue();
    return user.profili || [];
  }

  /* -------------------------------- USER ------------------------------- */
  
  /**
   * Recupera i consensi privacy dell'utente tramite chiamata API.
   * @returns Observable con i dati dei consensi privacy
   * @throws {IApiErrorResponse} Errore durante il recupero dei consensi
   */
  getApiUserPrivacy(): Observable<IConsensi> {
    let log_pref = this.log_label + '.getApiUserConsensi() ';
    Console.log(log_pref, 'inizio recupero consensi privacy', 'std');
    
    return this.apiService.get<IApiStandardResponse<IConsensi>>('user/consensi-privacy').pipe(
      map((response: IApiStandardResponse<IConsensi>) => {
        Console.log(log_pref, 'consensi privacy ricevuti: ' + JSON.stringify(response.data), 'std');
        return response.data;
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref, 'errore nel caricamento consensi privacy: ' + error, 'err');
        throw error;
      })
    );
  }

  /**
   * Recupera i consensi privacy di una specifica anagrafica tramite chiamata API.
   * @param id_anagrafica - ID dell'anagrafica di cui recuperare i consensi privacy
   * @returns Observable con i dati dei consensi privacy dell'anagrafica
   * @throws {IApiErrorResponse} Errore durante il recupero dei consensi
   */
  getApiAnagraficaPrivacy(id_anagrafica: number): Observable<IConsensi> {
    let log_pref = this.log_label + '.getApiAnagraficaConsensi() ';
    Console.log(log_pref, 'inizio recupero consensi privacy', 'std');
    
    return this.apiService.get<IApiStandardResponse<IConsensi>>('anagrafica/'+ id_anagrafica +'/consensi-privacy').pipe(
      map((response: IApiStandardResponse<IConsensi>) => {
        Console.log(log_pref, 'consensi privacy ricevuti: ' + JSON.stringify(response.data), 'std');
        return response.data;
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref, 'errore nel caricamento consensi privacy: ' + error, 'err');
        throw error;
      })
    );
  }

  /**
   * Carica i dati dell'utente corrente dal server e aggiorna il repository locale.
   * Verifica i diritti di accesso dell'utente e, in caso di diritti insufficienti, 
   * esegue automaticamente il logout. Se l'utente ha i diritti corretti, recupera 
   * anche i profili associati e i consensi privacy, poi li salva nel repository.
   * 
   * **Operazioni eseguite:**
   * 1. Recupera i dati utente dal server tramite API `/user`
   * 2. Verifica che il campo `roles` sia uguale a "user"
   * 3. Se i diritti non sono sufficienti, esegue logout automatico e termina con errore
   * 4. Se i diritti sono corretti, carica profili e consensi privacy in parallelo
   * 5. Aggiorna l'oggetto User con i dati recuperati (profili e consensi)
   * 6. Salva tutto nel repository locale
   * 7. Aggiorna i BehaviorSubject per notificare i componenti
   * 
   * **Controllo sicurezza:**
   * - Verifica che `response.data.roles === 'user'`
   * - In caso di fallimento, esegue `authService.signOut()` (SSO o legacy) e reindirizza
   * 
   * @returns Observable con i dati dell'utente aggiornati
   * @throws {Error} "Diritti insufficienti per accedere all'applicazione" se roles !== "user"
   * @throws {IApiErrorResponse} Errore durante il caricamento dei dati dal server
   */
  public getApiUser(): Observable<IUser> {
    let log_pref = this.log_label + '.loadUser() ';
    
    return this.apiService.get<IUserApiResponse>('user').pipe(
      switchMap((response) => {
        Console.log(log_pref, 'utente caricato, verifica diritti utente', 'std');
        
        // // Verifica dei diritti utente
        // if (response.data.roles !== 'user' && response.data.roles !== 'admin')  {
        //   Console.log(log_pref, `Diritti insufficienti. Roles ricevuto: ${response.data.roles}, richiesto: user o admin`, 'err');
        //   throw new Error('Diritti insufficienti per accedere all\'applicazione');
        // }
        
        // Console.log(log_pref, 'diritti verificati, caricamento profili e consensi privacy', 'std');

        // Recupera profili e consensi privacy in parallelo
        return forkJoin({
          profiles: this.getApiProfili(),
          consensi: this.getApiUserPrivacy()
        }).pipe(
          map(({ profiles, consensi }) => {
            // 1. Replace the profiles in the user data
            response.data.profili = profiles;
            
            // 2. Aggiorna i consensi privacy nell'utente
            response.data.privacy_trattamento_dati = consensi.privacy_trattamento_dati;
            response.data.privacy_dati_sensibili = consensi.privacy_dati_sensibili;
            response.data.privacy_attivita_commerciali = consensi.privacy_attivita_commerciali;
            response.data.privacy_attivita_conto_terzi = consensi.privacy_attivita_conto_terzi;
            
            // 3. Create a new User object with updated profiles and consensi
            const userObj = new User(response.data);
            
            // 4. Usa i nuovi metodi di sincronizzazione
            this.updateUserAndNotify(userObj);
            
            // 5. Aggiorna il profilo corrente se disponibile
            if (profiles && profiles.length > 0) {
              this.updateProfiloAndNotify(profiles[0]);
            }
            
            return response.data;
          })
        );
      }),
      catchError((error) => {
        Console.log(log_pref, 'errore nel caricamento utente: ' + error, 'err');
        throw error;
      })
    );
  }

  /**
   * Recupera un singolo profilo specifico tramite API, lo salva nel repository e aggiorna lo stato.
   * 
   * @param profilo - Tipo di profilo da recuperare ('promotore', 'convenzionato', 'manifestazione')
   * @returns Observable con i dati del profilo richiesto
   * @throws {IApiErrorResponse} Errore durante il recupero del profilo
   */
  getApiProfilo(profilo: ProfiloRuolo): Observable<IProfilo> {
    const log_pref = this.log_label + '.getApiProfiloSingolo() ';
    Console.log(log_pref, `Recupero profilo tramite /user/profile/${profilo}`, 'std');

    return this.apiService.get<{ message: string; data: IProfilo }>(`user/profile/${profilo}`).pipe(
      map((response) => {
        Console.log(log_pref, `Profilo ricevuto dall'API: ` + JSON.stringify(response.data), 'std');
        const profiloRicevuto = response.data;
        // Salva il profilo nel repository
        this.userRepository.saveProfilo(profiloRicevuto);
        // Aggiorna lo stato corrente
        this.updateProfiloAndNotify(profiloRicevuto);
        return profiloRicevuto;
      }),
      catchError((error) => {
        Console.log(log_pref, 'Errore nel recupero profilo: ' + error, 'err');
        throw error;
      })
    );
  }

  /**
   * Recupera tutti i profili associati all'utente corrente.
   * 
   * @returns Observable con array di tutti i profili utente
   * @throws {IApiErrorResponse} Errore durante il recupero dei profili
   */
  getApiProfili(): Observable<IProfilo[]> {
    const log_pref = this.log_label + '.getApiNewProfili() ';
    Console.log(log_pref, 'inizio recupero profili tramite /user/profiles', 'std');

    // Array of profile types to check
    const profileTypes: ProfiloRuolo[] = [
      'promotore',
      'convenzionato',
      'manifestazione',
    ];

    return this.apiService.get<{ message: string; data: IProfilo[] }>('user/profiles').pipe(
      map((response) => {
        Console.log(log_pref, 'Profili ricevuti dall\'API: ' + JSON.stringify(response.data), 'std');
        
        const receivedProfiles = response.data || [];
        const finalProfiles: IProfilo[] = [];

        // Per ogni tipo di profilo richiesto, verifica se esiste o crea uno nuovo
        profileTypes.forEach((profileType) => {
          const existingProfile = receivedProfiles.find(profile => profile.ruolo === profileType);
          
          if (existingProfile) {
            Console.log(log_pref, `Profilo ${profileType} trovato:` + JSON.stringify(existingProfile), 'std');
            finalProfiles.push(existingProfile);
          } else {
            Console.log(log_pref, `Profilo ${profileType} non trovato, ne creo uno nuovo`, 'std');
            finalProfiles.push(new Profilo(profileType));
          }
        });

        Console.log(log_pref, 'Profili finali: ' + JSON.stringify(finalProfiles), 'std');

        // Usa i nuovi metodi di sincronizzazione
        this.updateProfiliAndNotify(finalProfiles);
        
        // RRE non capisco cosa faccia questo
        // Update the current profile if we have at least one profile
        if (finalProfiles.length > 0) {
          this.updateProfiloAndNotify(finalProfiles[0]);
        }

        return finalProfiles;
      }),
      catchError((error) => {
        Console.log(log_pref, 'Errore nel recupero profili: ' + error, 'err');
        throw error;
      })
    );
  }

  /**
   * Verifica e completa una pre-registrazione esistente per un profilo specifico.
   * 
   * @param profilo_registrazione - Tipo di profilo da verificare
   * @param id_anagrafica - ID dell'anagrafica associata
   * @returns Observable con i dati del profilo verificato
   * @throws {IApiErrorResponse} Errore durante la verifica
   */
  verificaApiPreRegistrazione(
    profilo_registrazione: ProfiloRuolo,
    id_anagrafica: number
  ): Observable<IProfilo> {
    let log_pref =
      this.log_label +
      '.verificaApiPreRegistrazione(' +
      profilo_registrazione +
      ') ';
    switch (profilo_registrazione) {
      case 'manifestazione':
        return this.verificaApiPreRegistrazioneManifestazione();
      case 'convenzionato':
        return this.verificaApiPreRegistrazioneConvenzionato(id_anagrafica);
      case 'promotore':
        return this.verificaApiPreRegistrazionePromotore(id_anagrafica);
      default:
        return new Observable<IProfilo>();
    }
  }

  /**
   * Verifica una pre-registrazione per il profilo manifestazione.
   * 
   * @returns Observable con i dati del profilo manifestazione
   * @throws {IApiErrorResponse} Errore durante la verifica
   */
  verificaApiPreRegistrazioneManifestazione(): Observable<IProfilo> {
    return this.apiService.get<IApiProfilo>('manifestazione/iscrizione/verifica').pipe(
      map((response: IApiProfilo) => {
        Console.log(
          this.log_label + '.verificaApiPreRegistrazioneManifestazione() ',
          response.data,
          'std'
        );
        return response.data;
      }),
      catchError((error: IApiErrorResponse) => {
        // if (error.status_code === 449 || error.status_code === 448)
        // gestito da interceptor
        return throwError(() => error);
      })
    );
  }

  /**
   * Registra definitivamente l'utente al programma manifestazione/premi.
   * 
   * @returns Observable con i dati del profilo registrato
   * @throws {IApiErrorResponse} Errore durante la registrazione
   */
  registraApiManifestazioneAPremi(): Observable<IProfilo> {
    return this.apiService.get<IApiProfilo>('manifestazione/iscrizione').pipe(
      map((response: IApiProfilo) => response.data),
      catchError((error: IApiErrorResponse) => {
        // if (error.status_code === 449 || error.status_code === 448)
        // gestito da interceptor
        // Altri errori: rilancia normalmente
        return throwError(() => error);
      }),
      finalize(() => {
        // Operazioni di pulizia se necessarie
      })
    );
  }
  
  /**
   * Verifica se l'utente è iscritto a un determinato ruolo/profilo.
   * 
   * @param ruolo - Ruolo da verificare
   * @returns true se l'utente è iscritto al ruolo, false altrimenti
   */
  isIscritto(ruolo: ProfiloRuolo): boolean {
    const profilo = this.userRepository.getProfilo(ruolo);

    if (!profilo) return false;

    return profilo.ruolo === ruolo && profilo.bloccato === false;
  }

  /**
   * Verifica una pre-registrazione specifica per il profilo convenzionato.
   * 
   * @param id_anagrafica - ID dell'anagrafica del convenzionato
   * @returns Observable con la risposta API contenente i dati del profilo
   * @throws {IApiErrorResponse} Errore durante la verifica
   */
  verificaApiPreRegistrazioneConvenzionato(id_anagrafica: number): Observable<IProfilo> {
    return this.apiService.post< IApiStandardResponse<IProfilo>, { id_anagrafica: number }>
    ('convenzionato/iscrizione/verifica', { id_anagrafica }).pipe(
        map(response => response.data),
          catchError((error: IApiErrorResponse) => {
          // if (error.status_code === 449 || error.status_code === 448)
          // gestito da interceptor
          return throwError(() => error);
        })
      );
  }

  /**
   * Verifica una pre-registrazione specifica per il profilo promotore.
   * 
   * @param id_anagrafica - ID dell'anagrafica del promotore
   * @returns Observable con la risposta API contenente i dati del profilo
   * @throws {IApiErrorResponse} Errore durante la verifica
   */
  verificaApiPreRegistrazionePromotore(id_anagrafica: number): Observable<IProfilo> {
    return this.apiService.post<IApiStandardResponse<IProfilo>,{ id_anagrafica: number }>
    ('promotore/iscrizione/verifica', { id_anagrafica }).pipe(
      map(response => response.data),
      catchError((error: IApiErrorResponse) => {
        // if (error.status_code === 449 || error.status_code === 448)
        // gestito da interceptor
        return throwError(() => error);
    })
  );
  }

  /**
   * Registra un nuovo convenzionato.
   * 
   * @param id_anagrafica - ID dell'anagrafica del convenzionato
   * @returns Observable con la risposta API contenente i dati del profilo
   * @throws {IApiErrorResponse} Errore durante la registrazione
   */
  registraApiConvenzionato(id_anagrafica: number): Observable<IProfilo> {
    return this.apiService.post<IApiStandardResponse<IProfilo>, { id_anagrafica: number }>
    ('convenzionato/iscrizione', { id_anagrafica }).pipe(
      map(response => response.data),
      catchError((error: IApiErrorResponse) => {
        // if (error.status_code === 449 || error.status_code === 448)
        // gestito da interceptor
        return throwError(() => error);
      })
    );
  }

  /**
   * Registra un nuovo promotore.
   * 
   * @param id_anagrafica - ID dell'anagrafica del promotore
   * @returns Observable con la risposta API contenente i dati del profilo
   * @throws {IApiErrorResponse} Errore durante la registrazione
   */
  registraApiPromotore(id_anagrafica: number): Observable<IProfilo> {
    return this.apiService.post<IApiStandardResponse<IProfilo>, { id_anagrafica: number }>
    ('promotore/iscrizione', { id_anagrafica }).pipe(
      map(response => response.data),
      catchError((error: IApiErrorResponse) => {
        // if (error.status_code === 449 || error.status_code === 448)
        // gestito da interceptor
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna il contatto dell'utente (email o cellulare).
   * 
   * @param type - Tipo di contatto da aggiornare ('email' o 'sms')
   * @param newcontact - Nuovo contatto con conferma
   * @returns Observable con la risposta dell'API
   * @throws {IApiErrorResponse} Errore durante l'aggiornamento
   */
  setApiContatto(
    type: OtpChannel,
    newcontact:
      | { email: string; email_confirmation: string }
      | { cellulare: string; cellulare_confirmation: string }
  ) {
    return this.apiService
      .put<any, typeof newcontact>(
        `user/contatto/aggiorna/${type}`,
        newcontact
      )
      .pipe(
        map((data: any) => {
          let user_update = this.currentUser.getValue();
          if (type === 'cellulare' && 'cellulare' in newcontact) {
            user_update!.cellulare = newcontact.cellulare;
            user_update!.cellulare_verified_at = '1980-01-01 00:00:00';
          } else if ('email' in newcontact) {
            user_update!.email = newcontact.email;
            user_update!.email_verified_at = '1980-01-01 00:00:00';
          }

          // Update user and save to local storage
          this.userRepository.updateCurrentUser(this.currentUser, user_update!);
          this.userRepository.saveUser(user_update!);

          return data;
        }),
        catchError((error: IApiErrorResponse) => {
          const log_pref = this.log_label + '.setApiContatto() ';
          Console.log(log_pref + 'errore durante aggiornamento contatto:', error, 'err');
          // this.toastService.presentaToastErrore(
          //   `Errore durante l'aggiornamento del ${type === 'email' ? 'email' : 'numero di cellulare'}`
          // );
          throw error;
        })
      );
  }

  /* -------------------------------- INDIRIZZO ------------------------------- */

  /**
   * Recupera l'indirizzo di spedizione dell'utente.
   * 
   * @returns Observable con i dati dell'indirizzo di spedizione
   * @throws {IApiErrorResponse} Errore durante il recupero
   */
  getApiIndirizzoSpedizione(): Observable<IndirizzoSpedizione> {
    let log_pref = this.log_label + '.getApiIndirizzoSpedizione() ';
    
    // Prima controlliamo se l'indirizzo è già nello store
    const indirizzoStore = this.appStore.indirizzoSpedizione();
    if (indirizzoStore?.indirizzo !== undefined) {
      Console.log(log_pref + 'indirizzo trovato nello store', indirizzoStore);
      return of(indirizzoStore);
    }

    // Se non è nello store, lo recuperiamo dall'API
    return this.apiService.get<IApiGetIndirizzoSpedizione>('user/indirizzo/spedizione').pipe(
      map((response: IApiGetIndirizzoSpedizione) => {
        const indirizzo = new IndirizzoSpedizione();
        indirizzo.update({
          nominativo: response.data.nominativo,
          indirizzo: response.data.indirizzo,
          id_comune: response.data.id_comune,
          cap: response.data.cap,
          comune: new Comune({ 
            descrizione: response.data.comune, 
            cap: response.data.cap, 
            provincia: response.data.provincia 
          }),
        });
        
        // Salviamo l'indirizzo nello store
        this.appStore.setIndirizzoSpedizione(indirizzo);
        
        return indirizzo;
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref + 'errore', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera l'indirizzo di residenza dell'utente.
   * 
   * @returns Observable con i dati dell'indirizzo di residenza
   * @throws {IApiErrorResponse} Errore durante il recupero
   */
  getApiIndirizzoResidenza(): Observable<IndirizzoResidenza> {
    let log_pref = this.log_label + '.getApiIndirizzoResidenza() ';
    
    // Prima controlliamo se l'indirizzo è già nello store
    const indirizzoStore = this.appStore.indirizzoResidenza();
    if (indirizzoStore) {
      Console.log(log_pref + 'indirizzo trovato nello store', indirizzoStore);
      return of(indirizzoStore);
    }

    // Se non è nello store, lo recuperiamo dall'API
    return this.apiService.get<I_API_GET_IndirizzoResidenza>('user/indirizzo/residenza').pipe(
      map((response: I_API_GET_IndirizzoResidenza) => {
        const indirizzo = new IndirizzoResidenza();
        indirizzo.update({
          indirizzo: response.indirizzo_residenza,
          id_comune: response.id_comune_residenza,
          cap: response.cap_residenza,
          comune: response.comune_residenza,
          nazione: response.nazione_residenza,
          id_nazione: response.id_nazione_residenza,
        });

        // Salviamo l'indirizzo nello store
        this.appStore.setIndirizzoResidenza(indirizzo);
        
        return indirizzo;
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref + 'errore', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera l'indirizzo di domicilio dell'utente.
   * 
   * @returns Observable con i dati dell'indirizzo di domicilio
   * @throws {IApiErrorResponse} Errore durante il recupero
   */
  getApiIndirizzoDomicilio(): Observable<IndirizzoDomicilio> {
    let log_pref = this.log_label + '.getApiIndirizzoDomicilio() ';
    
    // Prima controlliamo se l'indirizzo è già nello store
    const indirizzoStore = this.appStore.indirizzoDomicilio();
    if (indirizzoStore) {
      Console.log(log_pref + 'indirizzo trovato nello store', indirizzoStore);
      return of(indirizzoStore);
    }

    // Se non è nello store, lo recuperiamo dall'API
    return this.apiService.get<I_API_GET_IndirizzoDomicilio>('user/indirizzo/domicilio').pipe(
      map((response: I_API_GET_IndirizzoDomicilio) => {
        const indirizzo = new IndirizzoDomicilio();
        indirizzo.update({
          nominativo: response.nominativo_domicilio,
          indirizzo: response.indirizzo_domicilio,
          id_comune: response.id_comune_domicilio,
          cap: response.cap_domicilio,
          comune: response.comune_domicilio,
        });

        // Salviamo l'indirizzo nello store
        this.appStore.setIndirizzoDomicilio(indirizzo);
        
        return indirizzo;
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref + 'errore', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna l'indirizzo di spedizione dell'utente.
   * 
   * @param indirizzo - Nuovi dati dell'indirizzo di spedizione
   * @returns Observable con la risposta dell'API
   * @throws {IApiErrorResponse} Errore durante l'aggiornamento
   */
  setApiIndirizzoSpedizione(
    indirizzo: IApiPutIndirizzoSpedizione
  ): Observable<IApiStandardResponse<void>> {
    let log_pref = this.log_label + '.setApiIndirizzoSpedizione() ';
    
    return this.apiService.put<IApiStandardResponse<IApiPutIndirizzoSpedizione>, IApiPutIndirizzoSpedizione>(
      'user/indirizzo/spedizione',
      indirizzo
    ).pipe(
      map((response: IApiStandardResponse<IApiPutIndirizzoSpedizione>): IApiStandardResponse<void> => ({
        message: response.message,
        data: undefined
      })),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref + 'errore:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna l'indirizzo di domicilio dell'utente.
   * 
   * @param indirizzo - Nuovi dati dell'indirizzo di domicilio
   * @returns Observable con la risposta dell'API
   * @throws {IApiErrorResponse} Errore durante l'aggiornamento
   */
  setApiIndirizzoDomicilio(
    indirizzo: I_API_PUT_IndirizzoDomicilio
  ): Observable<IApiStandardResponse<I_API_PUT_IndirizzoDomicilio>> {
    return this.apiService.put<IApiStandardResponse<I_API_PUT_IndirizzoDomicilio>, I_API_PUT_IndirizzoDomicilio>(
      'user/indirizzo/domicilio',
      indirizzo
    );
  }

  /**
   * Elimina l'indirizzo di domicilio dell'utente.
   * 
   * @returns Observable con la risposta dell'API
   * @throws {IApiErrorResponse} Errore durante l'eliminazione
   */
  delApiIndirizzoDomicilio(): Observable<any> {
    return this.apiService.delete<any>('user/indirizzo/domicilio');
  }

  /**
   * Aggiorna l'indirizzo di residenza dell'utente.
   * 
   * @param indirizzo - Nuovi dati dell'indirizzo di residenza
   * @returns Observable con la risposta dell'API
   * @throws {IApiErrorResponse} Errore durante l'aggiornamento
   */
  setApiIndirizzoResidenza(
    indirizzo: I_API_PUT_IndirizzoResidenza
  ): Observable<IApiStandardResponse<I_API_PUT_IndirizzoResidenza>> {
    return this.apiService.put<IApiStandardResponse<I_API_PUT_IndirizzoResidenza>, I_API_PUT_IndirizzoResidenza>(
      'user/indirizzo/residenza',
      indirizzo
    );
  }

  /* --------------------------------  CONSENSI PRIVACY ------------------------------- */

  /**
   * Aggiorna i consensi privacy dell'utente.
   * 
   * @param consensi - Nuovi consensi privacy da salvare
   * @returns Observable con la risposta dell'API
   * @throws {IApiErrorResponse} Errore durante l'aggiornamento
   */
  setApiUserPrivacy(consensi: IConsensi): Observable<IApiStandardResponse<IConsensi>> {
    let log_pref = this.log_label + '.setConsensi() ';
    return this.apiService
      .put<IApiStandardResponse<IConsensi>, IConsensi>('user/consensi-privacy', consensi)
      .pipe(
        map((risposta: IApiStandardResponse<IConsensi>) => {
          return risposta;
        }),
        catchError((error) => {
          Console.log(log_pref + 'errore:', error, 'err');
          // this.toastService.presentaToastErrore(
          //   "Errore durante l'aggiornamento dei consensi privacy"
          // );
          throw error;
        })
      );
  }

  /**
   * Aggiorna i consensi privacy di una specifica anagrafica tramite chiamata API.
   * 
   * @param id_anagrafica - ID dell'anagrafica per cui aggiornare i consensi privacy
   * @param consensi - Nuovi consensi privacy da salvare
   * @returns Observable con la risposta dell'API
   * @throws {IApiErrorResponse} Errore durante l'aggiornamento
   */
  setApiAnagraficaPrivacy(id_anagrafica: number, consensi: IConsensi): Observable<IApiStandardResponse<IConsensi>> {
    let log_pref = this.log_label + '.setConsensi() ';
    return this.apiService
      .put<IApiStandardResponse<IConsensi>, IConsensi>('anagrafica/'+ id_anagrafica +'/consensi-privacy', consensi)
      .pipe(
        map((risposta: IApiStandardResponse<IConsensi>) => {
          return risposta;
        }),
        catchError((error) => {
          Console.log(log_pref + 'errore:', error, 'err');
          // this.toastService.presentaToastErrore(
          //   "Errore durante l'aggiornamento dei consensi privacy"
          // );
          throw error;
        })
      );
  }

  /**
   * Verifica se esiste una registrazione promotore da completare.
   * 
   * @returns Observable boolean - true se c'è una registrazione da completare
   * @throws {IApiErrorResponse} Errore durante la verifica
   */
  isApiRegistrazionePromotoreDaCompletare():Observable<boolean> {
    let log_pref = this.log_label + '.isApiRegistrazionePromotoreDaCompletare() ';
    return this.apiService
      .get<I_API_GET_registrazionePromotoreDaCompletare>(
        'promotore/iscrizione/da-completare'
      )
      .pipe(
        map((response: I_API_GET_registrazionePromotoreDaCompletare) => {
          const result = !!(response.data?.id_anagrafica && response.data?.id_segnalatore);
          if (result) {
            Console.log(log_pref + 'Promotore da completare:', result);
          } else {
            Console.log(log_pref + 'Nessun Promotore da completare:', result);
          }
          return result;
        }),
        catchError((error) => {
          Console.log(log_pref + 'errore:', error, 'err');
          //throw error;
          return of(false);
        })
      );
  }

  /**
   * Verifica se esiste una registrazione convenzionato da completare.
   * 
   * @returns Observable boolean - true se c'è una registrazione da completare
   * @throws {IApiErrorResponse} Errore durante la verifica
   */
  isApiRegistrazioneConvenzionatoDaCompletare(): Observable<boolean> {
    let log_pref = this.log_label + '.isApiRegistrazioneConvenzionatoDaCompletare() ';
    return this.apiService
      .get<I_API_GET_registrazioneConvenzionatoDaCompletare>(
        'convenzionato/iscrizione/da-completare'
      )
      .pipe(
        map((response: I_API_GET_registrazioneConvenzionatoDaCompletare) => {
          const result = !!(response.data?.id_anagrafica && response.data?.id_segnalatore);
          if (result) {
            Console.log(log_pref + 'Convenzionato da completare:', result);
          } else {
            Console.log(log_pref + 'Nessun Convenzionato da completare:', result);
          }
          return result;
        }),
        catchError((error) => {
          Console.log(log_pref + 'errore:', error, 'err');
          //throw error;
          return of(false);
        })
      );
  }

  /**
   * Recupera l'elenco delle pratiche associate all'utente con paginazione.
   * 
   * @param pagination - Parametri di paginazione (offset, limit, ecc.)
   * @returns Observable con l'elenco paginato delle pratiche
   * @throws {IApiErrorResponse} Errore durante il recupero
   */
  getApiPraticheConvenzionato(pagination: IRequestPaginationData): Observable<IApiElencoPratiche> {
    const log_pref = this.log_label + 'UserService.getApiPratiche() ';
    Console.log(log_pref, 'inizio recupero pratiche', 'std');

    const queryParams = new URLSearchParams();
    const perPage = (pagination as any)?.per_page || 15;
    queryParams.append('per_page', perPage.toString());

    if (pagination.page) {
      queryParams.append('page', pagination.page.toString());
    }
    if (pagination.sort_by) {
      queryParams.append('sort_by', pagination.sort_by);
    }
    if (pagination.sort_order) {
      queryParams.append('sort_order', pagination.sort_order);
    }
    
    return this.apiService.get<IApiElencoPratiche>(
      `/convenzionato/pratiche?${queryParams.toString()}`
    ).pipe(
      catchError((error) => {
        Console.log(log_pref, 'errore nel recupero pratiche: ' + error, 'err');
        throw error;
      })
    );
  
  }

  /**
   * Recupera tutte le pratiche del promotore tramite chiamata API con paginazione completa.
   * @returns Observable con la risposta dell'API contenente tutte le pratiche in struttura gerarchica
   */
  public getApiPratichePromotore(): Observable<{ success: boolean, data: ISegnalatorePromotore[], message?: string }> {
    const log_pref = this.log_label + '.getPromotorePratiche() ';
    
    // Se USE_MOCK_DATA è true, restituisci i dati mock
    if (this.USE_MOCK_DATA) {
      Console.log(log_pref + 'Utilizzo dati mock', '', 'warn');
      return of({ success: true, data: MOCK_SEGNALATORI_PROMOTORE, message: 'Dati mock caricati' });
    }
    
    return this.fetchAllPaginatedData('promotore/pratiche').pipe(
      map(allData => {
        // Filtra gli elementi con livello 0 e costruisce la gerarchia
        const filteredData = allData.filter(item => item.livello !== 0);
        const hierarchicalData = this.buildHierarchy(filteredData);
        
        return {
          success: true,
          data: hierarchicalData,
          message: 'Tutti i dati caricati con successo'
        };
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref + 'errore nel recupero pratiche:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Scarica tutti i dati paginati da un endpoint API.
   * @param endpoint Endpoint API da chiamare
   * @returns Observable con tutti i dati combinati
   */
  private fetchAllPaginatedData(endpoint: string): Observable<ISegnalatorePromotore[]> {
    // Aggiungi per_page=100 alla prima chiamata
    const firstEndpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}per_page=100`;
    
    return this.apiService.get<IApiPromotorePratiche>(firstEndpoint).pipe(
      switchMap(firstResponse => {
        const totalPages = firstResponse.pagination.last_page;
        
        if (totalPages <= 1) {
          // Se c'è solo una pagina, restituisci direttamente i dati
          return of(firstResponse.data);
        }
        
        // Crea array di chiamate per tutte le pagine rimanenti
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const remainingRequests = remainingPages.map(page => 
          this.apiService.get<IApiPromotorePratiche>(`${endpoint}?page=${page}&per_page=100`)
        );
        
        // Combina la prima pagina con tutte le altre
        return forkJoin([
          of(firstResponse.data),
          ...remainingRequests
        ]).pipe(
          map(responses => {
            // Appiattisce tutti i risultati in un unico array
            const allData: ISegnalatorePromotore[] = [];
            
            responses.forEach((response, index) => {
              if (index === 0) {
                // Prima risposta è già un array di dati
                allData.push(...(response as ISegnalatorePromotore[]));
              } else {
                // Le altre risposte sono oggetti IApiPromotorePratiche
                const apiResponse = response as IApiPromotorePratiche;
                if (apiResponse.data) {
                  allData.push(...apiResponse.data);
                }
              }
            });
            
            return allData;
          })
        );
      })
    );
  }

  /**
   * Costruisce la struttura gerarchica dei segnalatori basata sui livelli e id_sponsor.
   * @param data Array di segnalatori ordinati per livello
   * @returns Array con struttura gerarchica
   */
  private buildHierarchy(data: ISegnalatorePromotore[]): ISegnalatorePromotore[] {
    const result: ISegnalatorePromotore[] = [];
    const nodeMap = new Map<number, ISegnalatorePromotore>();

    // Prima passata: crea tutti i nodi e inizializza children
    data.forEach(item => {
      const node = { ...item, children: [] };
      nodeMap.set(item.id_segnalatore, node);
    });

    // Seconda passata: costruisce la gerarchia
    data.forEach(item => {
      const node = nodeMap.get(item.id_segnalatore)!;
      
      // Se ha un sponsor (id_sponsor diverso da 0) e il sponsor esiste nella mappa
      if (item.id_sponsor && item.id_sponsor !== 0) {
        const parent = nodeMap.get(item.id_sponsor);
        if (parent) {
          parent.children!.push(node);
          return;
        }
      }
      
      // Se non ha sponsor o il sponsor non esiste, è un nodo root
      result.push(node);
    });

    return result;
  }

  /**
   * Recupera tutte le pratiche del convenzionato con paginazione automatica.
   * @returns Observable con la risposta dell'API contenente tutte le pratiche
   */
  public getConvenzionatoPratiche(): Observable<{ success: boolean, data: IPraticaConvenzionato[], message?: string }> {
    const log_pref = this.log_label + '.getConvenzionatoPratiche() ';
    
    // Se USE_MOCK_DATA è true, restituisci i dati mock
    if (this.USE_MOCK_DATA) {
      Console.log(log_pref + 'Utilizzo dati mock', '', 'warn');
      return of({ success: true, data: MOCK_PRATICHE_CONVENZIONATO, message: 'Dati mock caricati' });
    }
    
    return this.fetchAllPaginatedDataConvenzionato('convenzionato/pratiche').pipe(
      map(allData => ({
        success: true,
        data: allData,
        message: 'Tutti i dati caricati con successo'
      })),
      catchError((error: IApiErrorResponse) => {
        Console.log(log_pref + 'errore nel recupero pratiche:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Scarica tutti i dati paginati delle pratiche convenzionato da un endpoint API.
   * @param endpoint Endpoint API da chiamare
   * @returns Observable con tutti i dati combinati
   */
  private fetchAllPaginatedDataConvenzionato(endpoint: string): Observable<IPraticaConvenzionato[]> {
    // Aggiungi per_page=100 alla prima chiamata
    const firstEndpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}per_page=100`;
    
    return this.apiService.get<IApiConvenzionatoPratiche>(firstEndpoint).pipe(
      switchMap(firstResponse => {
        const totalPages = firstResponse.pagination.last_page;
        
        if (totalPages <= 1) {
          // Se c'è solo una pagina, restituisci direttamente i dati
          return of(firstResponse.data);
        }
        
        // Crea array di chiamate per tutte le pagine rimanenti
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const remainingRequests = remainingPages.map(page => 
          this.apiService.get<IApiConvenzionatoPratiche>(`${endpoint}?page=${page}&per_page=100`)
        );
        
        // Combina la prima pagina con tutte le altre
        return forkJoin([
          of(firstResponse.data),
          ...remainingRequests
        ]).pipe(
          map(responses => {
            // Appiattisce tutti i risultati in un unico array
            const allData: IPraticaConvenzionato[] = [];
            
            responses.forEach((response, index) => {
              if (index === 0) {
                // Prima risposta è già un array di dati
                allData.push(...(response as IPraticaConvenzionato[]));
              } else {
                // Le altre risposte sono oggetti IApiConvenzionatoPratiche
                const apiResponse = response as IApiConvenzionatoPratiche;
                if (apiResponse.data) {
                  allData.push(...apiResponse.data);
                }
              }
            });
            
            return allData;
          })
        );
      })
    );
  }
}
